<script setup lang="ts">
import { useRepairsContext } from '@/composables/rc/drawer-context'
import { useMinerPartRepairItems } from '@/composables/rc/miner-part-repair-items'
import { usePartDetails } from '@/composables/rc/part-details'
import { partTypes } from '@/types/rc/miners/PartType'
import { Icon } from '@iconify/vue'
import { last } from 'lodash'
import { computed, ref, toRefs, watchEffect } from 'vue'

const props = defineProps<{ failureId: number }>()

const selectedRepairItems = ref<(number | undefined)[]>([])

// Context - BE
const { minerId, selectedPart } = useRepairsContext()
const { isReplacement, repairItems, movedToInventory } = usePartDetails(minerId, selectedPart)
const { failureId } = toRefs(props)

// Source UI values from BE-provided data
watchEffect(() => {
  selectedRepairItems.value = repairItems.value
})

const { getRepairItemsQuery, createRepairItemMutation, deleteRepairItemMutation } = useMinerPartRepairItems(
  minerId,
  failureId
)

const { data: sourceRepairItems } = getRepairItemsQuery

const availableRepairItems = computed(
  () =>
    sourceRepairItems.value
      ?.filter((ri) => ri.part_type == partTypes[selectedPart.value])
      .filter((ri) => !selectedRepairItems.value.find((item) => item == ri.id)) ?? []
)

// Loading states targetting - ref to item's _id_
const itemBeingDeleted = ref<number>()
const itemBeingAdded = ref<number>()

const selectRepairItem = (repairItemId: number) => {
  itemBeingAdded.value = repairItemId
  createRepairItemMutation.mutate(
    { repair_item_id: repairItemId },
    {
      onSettled: () => (itemBeingAdded.value = undefined),
    }
  )
}

const deleteRepairItem = (idx: number) => {
  // If no number's there, we're deleting an empty select, just clear it from the state and return
  if (!selectedRepairItems.value[idx]) {
    selectedRepairItems.value.splice(idx, 1)
    return
  }

  // Delete on BE and clear from state on success
  itemBeingDeleted.value = selectedRepairItems.value[idx]
  deleteRepairItemMutation.mutate(
    { repair_item_id: selectedRepairItems.value[idx] },
    {
      onSuccess: () => selectedRepairItems.value.splice(idx, 1),
      onSettled: () => (itemBeingDeleted.value = undefined),
    }
  )
}
</script>

<template>
  <!-- Repair actions -->
  <div class="w-full flex flex-col gap-2">
    <el-text class="text-text-tertiary w-full">Repair actions</el-text>

    <!-- One select + trashcan per item in state array -->
    <div v-for="(itemId, idx) in selectedRepairItems" :key="itemId" class="flex gap-2 items-center">
      <el-select v-model="selectedRepairItems[idx]" :disabled="!!itemId" @change="selectRepairItem">
        <el-option
          v-for="repairItem in selectedRepairItems[idx] ? sourceRepairItems : availableRepairItems"
          :key="repairItem.id"
          :value="repairItem.id"
          :label="repairItem.description"
        />
      </el-select>
      <Icon v-if="!!itemId && itemBeingDeleted == itemId" :height="20" icon="eos-icons:three-dots-loading" />
      <Icon
        v-else-if="!movedToInventory"
        class="text-text-secondary hover:text-red cursor-pointer"
        :height="20"
        icon="mdi:trash-can-outline"
        @click="deleteRepairItem(idx)"
      />
    </div>

    <el-link
      class="self-start"
      type="primary"
      :disabled="(selectedRepairItems.length && !last(selectedRepairItems)) || isReplacement || movedToInventory"
      @click="selectedRepairItems.push(undefined)"
      ><Icon icon="mdi:plus" :height="20" /> Add repair actions</el-link
    >
  </div>
</template>
